import React, { useCallback, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { isEmpty, isEqual, omitBy } from 'lodash'

import makeStyles from '@mui/styles/makeStyles'
import {
  Button,
  ButtonGroup,
  Grid,
  Typography,
  Divider,
  Breadcrumbs,
  CircularProgress,
} from '@mui/material'

import { apiJSON } from '@shared/utils/api/api.util'
import { useRouter } from '@shared/hooks/use-router'
import { ProgressButton } from '@shared/components/progress-button.component'
import { TextField } from '@shared/components/text-field.component'
import { useThemeStyles } from '@shared/theme/use-theme-styles'
import { Audit, Location } from '@shared/types'

import { useCompanyQuery } from '@shared/hooks/use-company-query.hook'
import {
  OfflineEntityType,
  useOfflineContent,
} from '@shared/hooks/use-offline-content'
import { usePrevious } from '@shared/hooks/use-previous.hook'
import { registerInputRefFactory } from '@modules/admin-generic/admin-generic.utils'

type Fields<T, K = {}> = ({ key: keyof T; label: string } & K)[]

export const COMPANY_FIELDS: Fields<Location> = [
  { key: 'name', label: 'Klient' },
  { key: 'companyHeadquaters', label: 'Adres siedziby' },
]

export function MetricsPage() {
  const { query, navigate } = useRouter<{
    companyID: number
    auditID: number
  }>()
  const { companyID, auditID } = query
  const returnURL = `/company-profile/${companyID}/${auditID}`

  const offlineContent = useOfflineContent()
  const classes = useStyles()
  const themeClasses = useThemeStyles()
  const queryClient = useQueryClient()

  const { company } = useCompanyQuery(companyID)

  const { data: localAudit, isLoading: isLocalAuditLoading } = useQuery(
    `companies/${companyID}/Audits/${auditID}`,
    () => apiJSON(`companies/${companyID}/Audits/${auditID}`)
  )
  const offlineAudit = offlineContent.get<Audit>(OfflineEntityType.Audit)
  const audit = offlineAudit ?? localAudit
  const previousAudit = usePrevious(audit)

  const { register, handleSubmit, reset } = useForm<Audit>()
  const registerInputRef = registerInputRefFactory(register)

  useEffect(() => {
    if (!isEqual(previousAudit, audit)) {
      reset({
        ...omitBy(audit, isEmpty),
      })
    }
  }, [audit, previousAudit, reset])

  const { mutateAsync: updateAudit, isLoading: isAuditUpdating } = useMutation(
    (audit: Audit) =>
      apiJSON(`companies/${companyID}/Audits/${auditID}/metrics`, audit, {
        method: 'PUT',
      }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          `companies/${companyID}/Audits/${auditID}/metrics`
        )
      },
    }
  )

  const onSubmit = useCallback(
    async (data: Audit) => {
      try {
        const newAuditData = { ...audit, ...data }

        offlineContent.set(OfflineEntityType.Audit, newAuditData)

        await updateAudit(newAuditData)
      } catch (e) {
      } finally {
        navigate(returnURL)
      }
    },
    [updateAudit, audit, navigate, returnURL, offlineContent]
  )

  return audit && company && !isAuditUpdating && !isLocalAuditLoading ? (
    <>
      <div className={themeClasses.detailsContainer}>
        <h1 className={themeClasses.detailsHeader}>Metryka Raportu</h1>
        <Breadcrumbs className={themeClasses.detailsBreadcrumbsContainer}>
          {audit?.scopedRequirements?.map(
            (scope: { id: number; name: string }) => (
              <Typography
                key={scope.id}
                className={themeClasses.detailsBreadcrumbsItem}
              >
                {scope.name}
              </Typography>
            )
          )}
        </Breadcrumbs>
      </div>
      <Grid container>
        {COMPANY_FIELDS.map(({ label, key }) => (
          <>
            <Grid key={`${key}-label`} item xs={4}>
              <Typography variant="h6">{label}</Typography>
            </Grid>
            <Grid key={`${key}-value`} item xs={8}>
              <Typography variant="body2">{company[key]}</Typography>
            </Grid>
          </>
        ))}
        <Divider className={classes.divider} />
        <form noValidate id="metrics">
          <TextField
            {...registerInputRef('auditNumber')}
            rows={4}
            label="Numer raportu"
          />
          <TextField
            {...registerInputRef('legalStatusVersion')}
            rows={4}
            label="Wersja/Data stanu prawnego"
          />
          <TextField
            {...registerInputRef('preparationDate')}
            rows={4}
            label="Data oceny"
          />
          <TextField
            {...registerInputRef('reportDate')}
            rows={4}
            label="Data raportu"
          />
          <TextField
            {...registerInputRef('auditors')}
            rows={4}
            label="Audytorzy"
            multiline
          />
          <TextField
            {...registerInputRef('localization')}
            rows={4}
            label="Lokalizacja"
            multiline
          />
          <TextField
            {...registerInputRef('activities')}
            rows={4}
            label="Zakres działalności"
            multiline
          />
          <TextField
            {...registerInputRef('auditParticipants')}
            rows={4}
            label="Osoby uczestniczące w ocenie"
            multiline
          />
          <TextField
            {...registerInputRef('additionalInformation')}
            rows={4}
            label="Dodatkowe informacje"
            multiline
          />
          <Grid container justifyContent="space-between">
            <Grid item xs={6}>
              <TextField
                {...registerInputRef('introductionLeft')}
                rows={14}
                label="Wstęp kolumna lewa"
                multiline
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                {...registerInputRef('introductionRight')}
                rows={14}
                label="Wstęp kolumna prawa"
                multiline
              />
            </Grid>
          </Grid>
          <TextField
            {...registerInputRef('summary')}
            rows={4}
            label="Podsumowanie audytu"
            multiline
          />
        </form>
      </Grid>
      <ButtonGroup className={classes.actionButtonsContainer}>
        <Link to={returnURL} style={{ textDecoration: 'none' }}>
          <Button variant="outlined" color="primary">
            Anuluj
          </Button>
        </Link>
        <ProgressButton
          onClick={handleSubmit(onSubmit)}
          form="metrics"
          isLoading={isAuditUpdating}
          variant="contained"
          color="primary"
          className={classes.actionButtonSubmit}
        >
          Zapisz
        </ProgressButton>
      </ButtonGroup>
    </>
  ) : (
    <div className={classes.progress}>
      <CircularProgress />
    </div>
  )
}

export const useStyles = makeStyles((theme) => {
  /**
   * @todo possible styles duplication
   */
  return {
    divider: {
      width: '100%',
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),
    },
    actionButtonsContainer: {
      marginTop: '30px',
      marginLeft: 'auto',
      marginRight: '10px',
    },
    actionButtonSubmit: {
      marginLeft: 20,
    },
    progress: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
    },
  }
})
